import * as React from 'react';
import { LoaderCircle, MapPin } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { inputClass } from './ui';

interface Place {
  label: string;
  lat?: number;
  lon?: number;
}

interface AddressInputProps {
  id: string;
  value: string;
  onChange: (value: string, place?: Place) => void;
}

export function AddressInput({ id, value, onChange }: AddressInputProps) {
  const [results, setResults] = React.useState<Place[]>([]);
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(-1);
  const [searching, setSearching] = React.useState(false);
  const query = React.useRef('');

  React.useEffect(() => {
    const q = value.trim();
    if (q.length < 3 || q === query.current) return;
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(`/api/onboarding/places?q=${encodeURIComponent(q)}`, { signal: controller.signal });
        const body = (await res.json()) as { results: Place[] };
        setResults(body.results || []);
        setActive(-1);
      } catch {
        // Autocomplete is optional; typed addresses are accepted as-is.
      } finally {
        if (!controller.signal.aborted) setSearching(false);
      }
    }, 350);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
      setSearching(false);
    };
  }, [value]);

  const pick = (place: Place) => {
    query.current = place.label;
    onChange(place.label, place);
    setResults([]);
    setOpen(false);
  };

  const listId = `${id}-list`;
  const showList = open && results.length > 0;

  return (
    <div className="relative">
      <Input
        id={id}
        value={value}
        autoComplete="street-address"
        placeholder="Start typing the address"
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        className={cn(inputClass, 'pr-10')}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (!showList) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((i) => Math.min(i + 1, results.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
          } else if (e.key === 'Enter' && active >= 0) {
            e.preventDefault();
            pick(results[active]);
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
      />
      {searching && (
        <LoaderCircle aria-label="Searching" className="absolute right-3 top-3.5 h-5 w-5 animate-spin text-slate-400" />
      )}
      {showList && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-20 mt-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg"
        >
          {results.map((place, i) => (
            <li
              key={place.label}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(place)}
              className={cn(
                'flex cursor-pointer items-start gap-2 px-3 py-3 text-sm text-[#0A1628]',
                i === active ? 'bg-[#0A5CFF]/[0.06]' : 'hover:bg-slate-50',
              )}
            >
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#0A5CFF]" />
              <span>{place.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
